import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    ButtonTheme,
    ShowIf,
    TextBox,
    ToggleButton,
    ToggleButtonItem,
    ToggleButtonLabelSize,
    YesNoToggleButton,
} from '@apply/components';
import { ButtonGroupTypes } from 'devextreme-react/button-group';
import {
    useDeleteKeyEmployee,
    useKeyEmployeesQuery,
    useSaveKeyEmployee,
} from '../queries/businessProfileQueries';
import { KeyEmployee, WorkTimeType } from '../types';
import { PersonNameFields } from '../components/PersonNameFields';
import { SummaryCard, SummaryRow } from '../components/SummaryCard';
import { FormModal } from '../components/FormModal';
import { StepErrorBanner } from '../components/StepErrorBanner';
import { StepStatus } from '../components/StepStatus';
import { useActionError } from '../hooks/useActionError';
import { RegisterStep } from '../wizard/stepApi';
import { asInput } from '../components/inputValue';
import { useItemEditor } from '../hooks/useItemEditor';

interface KeyEmployeesStepProps {
    activeEntityId: number | null;
    onRegister: RegisterStep;
}

const WORK_TIME_ITEMS: ToggleButtonItem[] = [
    { id: 'Full', text: 'Full', hint: 'Full time' },
    { id: 'Part', text: 'Part', hint: 'Part time' },
];

export const KeyEmployeesStep: React.FC<KeyEmployeesStepProps> = ({ activeEntityId, onRegister }) => {
    const entityId = activeEntityId ?? 0;
    const { data: employees, isLoading, isError, error, refetch } = useKeyEmployeesQuery(entityId);
    const { mutateAsync: saveEmployee } = useSaveKeyEmployee();
    const { mutateAsync: deleteEmployee } = useDeleteKeyEmployee(entityId);
    const rowAction = useActionError();

    const [hasEmployees, setHasEmployees] = useState<boolean | null>(null);
    const existing = employees ?? [];

    const makeEmpty = useCallback(
        (): KeyEmployee => ({
            id: 0,
            entityId,
            firstName: '',
            middleName: '',
            lastName: '',
            suffix: '',
            title: '',
            phone: '',
            email: '',
            workTime: null,
            lastEditedUtc: null,
        }),
        [entityId],
    );

    const editor = useItemEditor<KeyEmployee>({
        makeEmpty,
        save: (employee) => saveEmployee({ ...employee, entityId }),
    });

    useEffect(() => {
        if (existing.length > 0 && hasEmployees === null) {
            setHasEmployees(true);
        }
    }, [existing.length, hasEmployees]);

    // Employees are saved from the popup, so Next has nothing to persist
    const save = useCallback(async () => undefined, []);

    useEffect(() => {
        onRegister({ save, canAdvance: hasEmployees === false || existing.length > 0 });
    }, [save, hasEmployees, existing.length, onRegister]);

    const update = <K extends keyof KeyEmployee>(key: K, value: KeyEmployee[K]) =>
        editor.setDraft((prior) => ({ ...prior, [key]: value }));

    const rows: SummaryRow[] = existing.map((employee) => ({
        key: String(employee.id),
        title: `${employee.firstName} ${employee.lastName}`.trim(),
        subtitle: employee.title,
        actions: [
            { label: 'Edit Details', onClick: () => editor.openEdit(employee) },
            {
                label: 'Delete Employee',
                destructive: true,
                onClick: () => {
                    void rowAction.run(() => deleteEmployee(employee.id));
                },
            },
        ],
    }));

    if (isLoading || isError) {
        return (
            <StepStatus
                isLoading={isLoading}
                isError={isError}
                error={error}
                onRetry={() => void refetch()}
            />
        );
    }

    return (
        <div className='bp-step'>
            {rowAction.error != null && (
                <StepErrorBanner
                    error={rowAction.error}
                    onDismiss={rowAction.clear}
                />
            )}
            <ShowIf showIf={existing.length === 0}>
                <YesNoToggleButton
                    id='bpHasKeyEmployees'
                    value={hasEmployees ?? undefined}
                    onItemClick={(e: ButtonGroupTypes.ItemClickEvent) =>
                        setHasEmployees((e.itemData as ToggleButtonItem)?.id === 'Yes')
                    }
                    width='230px'
                    height='40px'
                    label='Are there any key employees that you would like to have access to Draw Requests?'
                    labelSize={ToggleButtonLabelSize.Medium}
                />
            </ShowIf>

            <ShowIf showIf={existing.length > 0}>
                <SummaryCard
                    id='bpKeyEmployees'
                    heading='Employees'
                    rows={rows}
                />
            </ShowIf>

            <ShowIf showIf={hasEmployees === true}>
                <div style={{ marginTop: 24 }}>
                    <Button
                        id='bpAddEmployeeButton'
                        dataTestid='bpAddEmployeeButton'
                        theme={existing.length === 0 ? ButtonTheme.Primary : ButtonTheme.Secondary}
                        width='100%'
                        maxWidth='346px'
                        handleClick={editor.openAdd}
                    >
                        {existing.length === 0 ? 'Add Employee' : 'Add Another Employee'}
                    </Button>
                </div>
            </ShowIf>

            <FormModal
                id='bpEmployeeModal'
                isOpen={editor.isOpen}
                title={editor.isEditing ? 'Edit Key Employee' : 'Add Key Employee'}
                onSave={editor.commit}
                onClose={editor.close}
            >
                <PersonNameFields
                    idPrefix='bpEmployee'
                    value={editor.draft}
                    requiredFields
                    onChange={(patch) => editor.setDraft((prior) => ({ ...prior, ...patch }))}
                />

                <TextBox
                    id='bpEmployeeTitle'
                    value={asInput(editor.draft.title)}
                    onChange={(v) => update('title', v)}
                    label='Title'
                    placeholder='Enter'
                    maxLength={50}
                />

                <TextBox
                    id='bpEmployeePhone'
                    value={asInput(editor.draft.phone)}
                    onChange={(v) => update('phone', v)}
                    label='Phone Number'
                    type='tel'
                    maxLength={20}
                />

                <TextBox
                    id='bpEmployeeEmail'
                    value={asInput(editor.draft.email)}
                    onChange={(v) => update('email', v)}
                    label='Email Address'
                    type='email'
                    maxLength={200}
                />

                <div style={{ marginTop: 20 }}>
                    <ToggleButton
                        id='bpEmployeeWorkTime'
                        items={WORK_TIME_ITEMS}
                        selectedItems={
                            editor.draft.workTime === null ? [] : [WorkTimeType[editor.draft.workTime]]
                        }
                        selectionMode={editor.draft.workTime === null ? 'none' : 'single'}
                        stylingMode='outlined'
                        onItemClick={(e: ButtonGroupTypes.ItemClickEvent) =>
                            update(
                                'workTime',
                                (e.itemData as ToggleButtonItem)?.id === 'Full'
                                    ? WorkTimeType.Full
                                    : WorkTimeType.Part,
                            )
                        }
                        width='230px'
                        height='40px'
                        label='Is this person full time or part time?'
                        labelSize={ToggleButtonLabelSize.Medium}
                    />
                </div>
            </FormModal>
        </div>
    );
};
